import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { useTranslation } from '@/hooks/use-translation';
import type { OptionalNumber } from '@/lib/piano/touch-design/measured-key.requirements';
import { feltWeight } from '@/lib/piano/touch-design/felt-balance-weight';
import type { TouchWeightAnalyzedKeyboard } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import { useGenerateSerie } from '../../hooks/use-generate-serie';
import type { TouchDesignSerie } from '../../interfaces';
import { TouchDesignSerieVariant } from '../../KeyboardChart';

/** The four quantities of the figure, in the order the legend lists them. */
export const TOUCH_WEIGHT_SERIE_IDS = ['down', 'up', 'balance', 'friction'] as const;
export type TouchWeightSerieId = (typeof TOUCH_WEIGHT_SERIE_IDS)[number];
export type TouchWeightSeries = Record<TouchWeightSerieId, TouchDesignSerie>;

/**
 * What the static balance figure draws: down and up weight, the balance
 * between them, and the friction that separates them.
 *
 * Series are kept whole — one entry per key, gaps included — so the reading
 * strip at the head of the chart can read a key off the very series that was
 * drawn.
 */
export const useTouchWeightSeries = (analyzedKeyboard, t0) => {
  const $ = _c(28);
  const useSupportSpringMeasurements = t0 === undefined ? true : t0;
  const {
    t
  } = useTranslation();
  const {
    generateSerie
  } = useGenerateSerie(analyzedKeyboard);
  let t1;
  if ($[0] !== useSupportSpringMeasurements) {
    t1 = (weight, supportSpringBalanceWeight) => feltWeight(weight, useSupportSpringMeasurements ? supportSpringBalanceWeight : null);
    $[0] = useSupportSpringMeasurements;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const withoutSpringSupport = t1;
  let t2;
  if ($[2] !== withoutSpringSupport) {
    t2 = key => withoutSpringSupport(key.payload.balanceWeight, key.payload.supportSpringBalanceWeight);
    $[2] = withoutSpringSupport;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== generateSerie || $[5] !== t || $[6] !== t2) {
    t3 = generateSerie(t2, t("Balance"), TouchDesignSerieVariant.Measured);
    $[4] = generateSerie;
    $[5] = t;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== withoutSpringSupport) {
    t4 = key_0 => withoutSpringSupport(key_0.payload.downWeightWithoutSpringSupport, key_0.payload.supportSpringBalanceWeight);
    $[8] = withoutSpringSupport;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== generateSerie || $[11] !== t || $[12] !== t4) {
    t5 = generateSerie(t4, t("Down"), TouchDesignSerieVariant.Measured);
    $[10] = generateSerie;
    $[11] = t;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== generateSerie || $[15] !== t) {
    t6 = generateSerie(_temp, t("Friction"), TouchDesignSerieVariant.Measured);
    $[14] = generateSerie;
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== withoutSpringSupport) {
    t7 = key_2 => withoutSpringSupport(key_2.payload.upWeight, key_2.payload.supportSpringBalanceWeight);
    $[17] = withoutSpringSupport;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== generateSerie || $[20] !== t || $[21] !== t7) {
    t8 = generateSerie(t7, t("Up"), TouchDesignSerieVariant.Measured);
    $[19] = generateSerie;
    $[20] = t;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t3 || $[24] !== t5 || $[25] !== t6 || $[26] !== t8) {
    t9 = {
      balance: t3,
      down: t5,
      friction: t6,
      up: t8
    };
    $[23] = t3;
    $[24] = t5;
    $[25] = t6;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
};
function _temp(key_1) {
  return key_1.payload.frictionWeight;
}